/**
 * Shared particle color resolution for Canvas2D + WebGL (Track Z1 / C1 + R type).
 */

import { buildCycleColors, type CycleColorMaps } from "../analysis/cycleColor";
import type { SimView } from "../core/simView";
import {
  effectiveColorMode,
  type ColorMode,
  type RenderParams,
} from "../sim/params";

const UNIFORM_RGB: [number, number, number] = [0.89, 0.91, 0.94];

/** Categorical palette for node types (up to T=8). */
const TYPE_PALETTE: readonly [number, number, number][] = [
  [0.35, 0.75, 1.0], // blue
  [1.0, 0.55, 0.25], // orange
  [0.45, 0.9, 0.5], // green
  [0.85, 0.45, 0.9], // purple
  [1.0, 0.85, 0.3], // yellow
  [0.4, 0.9, 0.9], // cyan
  [1.0, 0.45, 0.55], // rose
  [0.7, 0.7, 0.75], // gray
];

/** Parse #rrggbb or rgb(r,g,b) → 0..1 RGB. */
export function cssToRgb01(css: string): [number, number, number] {
  const hex = /^#?([0-9a-f]{6})$/i.exec(css.trim());
  if (hex) {
    const n = parseInt(hex[1]!, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i.exec(css.trim());
  if (rgb) {
    return [Number(rgb[1]) / 255, Number(rgb[2]) / 255, Number(rgb[3]) / 255];
  }
  return UNIFORM_RGB;
}

export type ParticleColorPlan =
  | { mode: "velocity" }
  | { mode: "uniform"; rgb: [number, number, number] }
  | { mode: "cycle"; rgb: Float32Array; maps: CycleColorMaps }
  | { mode: "type"; rgb: Float32Array };

/**
 * Resolve how points should be colored this frame.
 * `cycleCache` / epoch: pass previous cache to avoid rebuild when topologyEpoch stable.
 */
export function resolveParticleColorPlan(
  view: SimView,
  render: RenderParams,
  cache?: {
    epoch: number;
    friends: Int32Array | null;
    maps: CycleColorMaps | null;
  },
): ParticleColorPlan {
  const mode: ColorMode = effectiveColorMode(render);
  if (mode === "velocity") return { mode: "velocity" };
  if (mode === "uniform") return { mode: "uniform", rgb: UNIFORM_RGB };

  if (mode === "type") {
    const n = view.n;
    const rgb = new Float32Array(n * 3);
    const types = view.types;
    for (let i = 0; i < n; i++) {
      const t = types ? types[i]! : 0;
      const [r, g, b] = TYPE_PALETTE[t % TYPE_PALETTE.length]!;
      rgb[i * 3] = r;
      rgb[i * 3 + 1] = g;
      rgb[i * 3 + 2] = b;
    }
    return { mode: "type", rgb };
  }

  let maps: CycleColorMaps;
  if (
    cache &&
    cache.maps &&
    cache.epoch === view.topologyEpoch &&
    cache.friends === view.friends
  ) {
    maps = cache.maps;
  } else {
    maps = buildCycleColors(view.friends);
    if (cache) {
      cache.epoch = view.topologyEpoch;
      cache.friends = view.friends;
      cache.maps = maps;
    }
  }

  const n = view.n;
  const rgb = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const [r, g, b] = cssToRgb01(maps.css[i] ?? "#94a3b8");
    rgb[i * 3] = r;
    rgb[i * 3 + 1] = g;
    rgb[i * 3 + 2] = b;
  }
  return { mode: "cycle", rgb, maps };
}
